// Tipos y utilidades del editor de campañas (espejo del modelo zod de la API:
// apps/api/src/lib/mkt-email-render.ts).
export type Align = "left" | "center" | "right";
export type SocialNetwork = "facebook" | "instagram" | "linkedin" | "x" | "youtube" | "tiktok" | "whatsapp" | "web";
export type FontKey = "arial" | "helvetica" | "verdana" | "tahoma" | "trebuchet" | "georgia" | "times" | "courier";

export type Block =
  | { id: string; type: "heading"; text: string; level: 1 | 2 | 3; align: Align; color?: string; fontSize?: number; padding?: number }
  | { id: string; type: "paragraph"; html: string; align: Align; color?: string; fontSize?: number; padding?: number }
  | { id: string; type: "image"; src: string; alt: string; href: string; width: number; align: Align; padding?: number }
  | { id: string; type: "button"; text: string; href: string; bg: string; color: string; radius: number; align: Align; fullWidth: boolean; fontSize?: number; padding?: number }
  | { id: string; type: "divider"; color: string; thickness: number; style: "solid" | "dashed" | "dotted"; width: number; padding?: number }
  | { id: string; type: "spacer"; height: number }
  | { id: string; type: "qr"; data: string; size: number; color: string; align: Align; padding?: number }
  | { id: string; type: "html"; html: string }
  | { id: string; type: "social"; align: Align; size: number; items: Array<{ network: SocialNetwork; url: string }>; padding?: number };
export type BlockType = Block["type"];

export interface Column { id: string; blocks: Block[] }
export interface Row { id: string; columns: Column[]; ratios: number[]; bg?: string | null; padding: number; stackOnMobile?: boolean }
export interface EmailBody { width: number; bg: string; contentBg: string; textColor: string; linkColor: string; font: FontKey; align: "left" | "center" }
export interface EmailDesign { version: 1; body: EmailBody; rows: Row[] }

export type CampaignStatus = "draft" | "scheduled" | "sending" | "sent" | "cancelled";
export interface CampaignStats {
  recipients?: number; sent?: number; delivered?: number; failed?: number; bounces?: number; skipped?: number; pending?: number;
  opens?: number; uniqueOpens?: number; clicks?: number; uniqueClicks?: number; unsubscribes?: number;
}
export interface CampaignRow {
  id: string; name: string; channel: string; status: CampaignStatus; subject: string | null; folder: string | null;
  scheduledAt: string | null; sentAt: string | null; stats: CampaignStats; source: string; createdAt: string; updatedAt: string;
  hasHtml: boolean; creator: string | null;
}
export interface AuditIssue { level: "error" | "warning" | "info"; message: string; blockId?: string }
export interface Campaign extends Omit<CampaignRow, "creator"> {
  preheader: string | null; fromName: string | null; replyTo: string | null; design: EmailDesign | null;
  listIds: string[]; excludeListIds: string[]; creator: string | null;
  lists: Array<{ id: string; name: string }>; excludeLists: Array<{ id: string; name: string }>; audit: AuditIssue[];
}
export interface AudienceList { id: string; name: string; kind: string; description: string | null; source: string; creator: string | null; contacts: number; emails: number }
export interface MediaItem { id: string; filename: string; contentType: string; sizeBytes: number; createdAt: string; url: string }

export const STATUS_LABEL: Record<CampaignStatus, { label: string; color: string }> = {
  draft: { label: "Borrador", color: "gray" },
  scheduled: { label: "Planificada", color: "purple" },
  sending: { label: "Enviando", color: "amber" },
  sent: { label: "Enviada", color: "green" },
  cancelled: { label: "Cancelada", color: "red" },
};

export const FONT_OPTIONS: Array<{ key: FontKey; label: string; css: string }> = [
  { key: "arial", label: "Arial", css: "Arial, Helvetica, sans-serif" },
  { key: "helvetica", label: "Helvetica", css: "Helvetica, Arial, sans-serif" },
  { key: "verdana", label: "Verdana", css: "Verdana, Geneva, sans-serif" },
  { key: "tahoma", label: "Tahoma", css: "Tahoma, Verdana, sans-serif" },
  { key: "trebuchet", label: "Trebuchet MS", css: "'Trebuchet MS', Helvetica, sans-serif" },
  { key: "georgia", label: "Georgia", css: "Georgia, 'Times New Roman', serif" },
  { key: "times", label: "Times New Roman", css: "'Times New Roman', Times, serif" },
  { key: "courier", label: "Courier New", css: "'Courier New', Courier, monospace" },
];

export const SOCIAL_META: Record<SocialNetwork, { label: string; bg: string; name: string }> = {
  facebook: { label: "f", bg: "#1877F2", name: "Facebook" },
  instagram: { label: "IG", bg: "#E1306C", name: "Instagram" },
  linkedin: { label: "in", bg: "#0A66C2", name: "LinkedIn" },
  x: { label: "X", bg: "#000000", name: "X" },
  youtube: { label: "YT", bg: "#FF0000", name: "YouTube" },
  tiktok: { label: "TT", bg: "#111111", name: "TikTok" },
  whatsapp: { label: "WA", bg: "#25D366", name: "WhatsApp" },
  web: { label: "www", bg: "#475569", name: "Sitio web" },
};

export const VARIABLES: Array<{ key: string; label: string }> = [
  { key: "{{nombre}}", label: "Nombre" },
  { key: "{{apellido}}", label: "Apellido" },
  { key: "{{empresa}}", label: "Empresa" },
  { key: "{{email}}", label: "Email" },
  { key: "{{baja_url}}", label: "URL de baja" },
];

export const uid = (): string => Math.random().toString(36).slice(2, 10);

export const BLOCK_LABEL: Record<BlockType, string> = {
  heading: "Encabezado", paragraph: "Párrafo", image: "Imagen", button: "Botón", divider: "Divisor",
  spacer: "Espaciador", qr: "Código QR", html: "HTML", social: "Redes sociales",
};

export function newBlock(type: BlockType): Block {
  const id = uid();
  switch (type) {
    case "heading": return { id, type, text: "Título", level: 2, align: "left" };
    case "paragraph": return { id, type, html: "<p>Escribe tu texto aquí.</p>", align: "left" };
    case "image": return { id, type, src: "", alt: "", href: "", width: 100, align: "center" };
    case "button": return { id, type, text: "Ver más", href: "https://", bg: "#1e3a8a", color: "#ffffff", radius: 6, align: "center", fullWidth: false };
    case "divider": return { id, type, color: "#e5e7eb", thickness: 1, style: "solid", width: 100 };
    case "spacer": return { id, type, height: 24 };
    case "qr": return { id, type, data: "https://", size: 150, color: "#000000", align: "center" };
    case "html": return { id, type, html: "<p>HTML personalizado</p>" };
    case "social": return { id, type, align: "center", size: 32, items: [{ network: "facebook", url: "https://facebook.com/" }, { network: "linkedin", url: "https://linkedin.com/" }, { network: "instagram", url: "https://instagram.com/" }] };
  }
}

/** Plantillas de filas (1-4 columnas). */
export const ROW_LAYOUTS: Array<{ label: string; ratios: number[] }> = [
  { label: "1 columna", ratios: [1] },
  { label: "2 columnas", ratios: [1, 1] },
  { label: "1/3 + 2/3", ratios: [1, 2] },
  { label: "2/3 + 1/3", ratios: [2, 1] },
  { label: "3 columnas", ratios: [1, 1, 1] },
  { label: "4 columnas", ratios: [1, 1, 1, 1] },
];
export const newRow = (ratios: number[]): Row => ({ id: uid(), ratios, padding: 10, columns: ratios.map(() => ({ id: uid(), blocks: [] })) });

/** Copia profunda con ids nuevos (duplicar bloques/filas). */
export function cloneBlock(b: Block): Block { return { ...structuredClone(b), id: uid() }; }
export function cloneRow(r: Row): Row {
  return { ...structuredClone(r), id: uid(), columns: r.columns.map((c) => ({ id: uid(), blocks: c.blocks.map(cloneBlock) })) };
}

export const pct = (n: number | undefined, total: number | undefined): string =>
  !total ? "0%" : `${(((n ?? 0) / total) * 100).toLocaleString("es-CO", { maximumFractionDigits: 1 })}%`;
export const fmtDate = (s: string | null): string =>
  s ? new Date(s).toLocaleString("es-CO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
